import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import i18n from "i18next";
import "./_ActionsRibbon.scss";
import Alert from "../../common/alert/Alert";
import Clipboard from "../../clipboard/Clipboard";
import { isSlide, isBox } from "../../../../common/utils";
import { LuUndo2, LuRedo2, LuCopy, LuCopyPlus, LuPen, LuZoomIn, LuZoomOut } from "react-icons/lu";
import ThemePaletteModal from "../scene_theme_palette/ThemePaletteModal";
import { useDispatch } from "react-redux";
import IdealStudio from "../../../../core/editor/main";
import { updateBox } from "../../../../common/actions";
import HtmlEditModal from "../../common/html_edit_modal/HtmlEditModal";
import { resolveEditAction } from "./resolveEditAction";

const ActionsRibbon = (props) => {
  const [clipboardAlert, setClipboardAlert] = useState(false);
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [showHtmlEditModal, setShowHtmlEditModal] = useState(false);
  const dispatch = useDispatch();
  const holderRef = useRef(null);

  const isPage = () => {
    return props.containedViews[props.containedViewSelected]
      ? props.containedViews[props.containedViewSelected]
      : props.navItems[props.navItemSelected]
        ? props.navItems[props.navItemSelected]
        : null;
  };

  const onClick = () => {
    setClipboardAlert((prev) => !prev);
    return true;
  };

  const onCanvasZoom = (direction) => {
    if (typeof props.onCanvasZoom === "function") {
      props.onCanvasZoom(direction);
    }
  };

  const selectedBoxObj = props.boxSelected && props.boxes ? props.boxes[props.boxSelected] : null;
  const selectedPluginToolbar =
    props.boxSelected && props.pluginToolbars ? props.pluginToolbars[props.boxSelected] : null;
  const selectedPluginConfig =
    selectedPluginToolbar && IdealStudio.Plugins.get(selectedPluginToolbar.pluginId)
      ? IdealStudio.Plugins.get(selectedPluginToolbar.pluginId).getConfig()
      : null;
  const editAction = resolveEditAction({
    boxSelected: props.boxSelected,
    boxSelectedAll: props.boxSelectedAll,
    box: selectedBoxObj,
    pluginToolbar: selectedPluginToolbar,
    pluginConfig: selectedPluginConfig,
  });
  const editDisabled = editAction.kind === "none";
  const editTooltipKey = editDisabled
    ? `EditButton.${editAction.disabledReason.replace(/-/g, "_")}`
    : "EditButton.edit_selected";

  // TODO document.queryCommandSupported(act.key)
  const clipboardActions = [
    {
      key: "canvas-zoom-out",
      disabled:
        typeof props.canvasZoom === "number" &&
        typeof props.canvasZoomMin === "number" &&
        props.canvasZoom <= props.canvasZoomMin + 1e-6,
      icon: "zoom_out",
      i18nkey: "clipboard.zoomOut",
      onClick: () => onCanvasZoom("out"),
    },
    {
      key: "canvas-zoom-in",
      disabled:
        typeof props.canvasZoom === "number" &&
        typeof props.canvasZoomMax === "number" &&
        props.canvasZoom >= props.canvasZoomMax - 1e-6,
      icon: "zoom_in",
      i18nkey: "clipboard.zoomIn",
      onClick: () => onCanvasZoom("in"),
    },
    {
      key: "undo",
      disabled: props.undoDisabled,
      icon: "undo",
      i18nkey: "Undo",
      onClick: props.onUndo,
    },
    {
      key: "redo",
      disabled: props.redoDisabled,
      icon: "redo",
      i18nkey: "Redo",
      onClick: props.onRedo,
    },
    {
      key: "copyPaste",
      disabled: !(props.boxSelected && isBox(props.boxSelected)),
      icon: "content_copy",
      i18nkey: "clipboard.copyPaste",
      onClick: onClick,
    },
    // { key: "cut", disabled: !(props.boxSelected && isBox(props.boxSelected)), icon: "content_cut", i18nkey: "clipboard.cut", onClick: onClick },
    // { key: "paste", disabled: !(props.boxSelected && isBox(props.boxSelected)), icon: "content_paste", i18nkey: "clipboard.paste", onClick: onClick },
    {
      key: "duplicate",
      disabled: !(props.boxSelected && isBox(props.boxSelected)),
      icon: "content_copy",
      i18nkey: "clipboard.duplicate",
      onClick: onClick,
    },
    {
      key: "Edit",
      i18nkey: editTooltipKey,
      icon: "edit",
      disabled: editDisabled,
      onClick: () => {
        if (editDisabled) return;
        // Always route the canvas Edit button to HtmlEditModal — it's the
        // single "new library" UI for editing the selected element.
        // The legacy paths (inline CKEditor via onTextEditorToggled, the file
        // picker via openFileModal, the plugin config modal via openConfigModal)
        // remain reachable from the per-element shortcut bar (EditorShortcuts);
        // this top-right pen must not open them.
        setShowHtmlEditModal(true);
      },
    },
  ];

  const page = isPage();
  const slide = page && isSlide(page.type);
  const container = 0;
  const box_layer = page.boxes.indexOf(props.boxSelected);
  const selectedBox = props.boxSelected !== -1 ? props.boxes[props.boxSelected] : null;
  const isSceneSound =
    (selectedBox &&
      (selectedBox.pluginId === "scene-audio" ||
        (selectedBox.type && selectedBox.type === "scene-audio"))) ||
    props.boxSelected === "scene-audio";
  const disable_bt =
    (props.boxSelected !== -1 && selectedBox && selectedBox.parent && isBox(selectedBox.parent)) ||
    props.boxSelected === -1 ||
    page.boxes.length === 1 ||
    isSceneSound;
  const boxes = page.boxes;

  // TODO:revisar este código para que puedan funcionar las capas en los documentos (posición absoluta combinada con relativa...mal)
  // let index = Object.keys(props.boxes).length;
  /* if (!slide && props.boxes[props.boxSelected] !== undefined && index > 1) {
            container = props.boxes[props.boxSelected].container;
            boxes = props.boxes[page.boxes[0]].sortableContainers[container].children;
            box_layer = props.boxes[page.boxes[0]].sortableContainers[container].children.indexOf(props.boxSelected);
            disable_bt = props.boxSelected === -1 || page.boxes.length === 2;
        } */
  const layerActions = [
    {
      key: "BringtoFront",
      i18nkey: "order.BringtoFront",
      icon: "flip_to_front",
      disabled: disable_bt || box_layer === boxes.length - 1,
      onClick: () => {
        props.onBoxLayerChanged(props.boxSelected, page.id, container, "front", boxes);
      },
    },
    {
      key: "Ahead",
      i18nkey: "order.Ahead",
      icon: "flip_to_front",
      disabled: disable_bt || box_layer === boxes.length - 1,
      onClick: () => {
        props.onBoxLayerChanged(props.boxSelected, page.id, container, "ahead", boxes);
      },
    },
    {
      key: "Behind",
      i18nkey: "order.Behind",
      icon: "flip_to_back",
      disabled: disable_bt || box_layer === 0,
      onClick: () => {
        props.onBoxLayerChanged(props.boxSelected, page.id, container, "behind", boxes);
      },
    },
    {
      key: "SendtoBack",
      i18nkey: "order.SendtoBack",
      icon: "flip_to_back",
      disabled: disable_bt || box_layer === 0,
      onClick: () => {
        props.onBoxLayerChanged(props.boxSelected, page.id, container, "back", boxes);
      },
    },
    "separator",
    {
      key: "Container",
      i18nkey: "Grid",
      icon: "grid_on",
      disabled: false,
      onClick: props.onContainerToggle,
    },
    "separator",
    {
      key: "Theme",
      i18nkey: "ThemePalette.Theme",
      icon: "theme",
      disabled: false,
      onClick: (e) => {
        setShowThemeModal(true);
        document.activeElement.blur();
      },
    },
  ];

  const button = (act) => {
    return (
      <OverlayTrigger
        key={act.key}
        placement="bottom"
        overlay={
          <Tooltip id={`clipboard-tooltip-${act.key}`}>{i18n.t(act.i18nkey) || act.key}</Tooltip>
        }
      >
        <button
          className={act.key === "Container" && props.grid ? "ActionBtn active" : "ActionBtn"}
          disabled={act.disabled}
          name={act.key}
          onClick={(e) => {
            act.onClick(e);
            document.activeElement.blur();
          }}
        >
          {act.key === "undo" ? (
            <LuUndo2 size={22} />
          ) : act.key === "redo" ? (
            <LuRedo2 size={22} />
          ) : act.key === "copyPaste" ? (
            <LuCopy size={22} />
          ) : act.key === "duplicate" ? (
            <LuCopyPlus size={22} />
          ) : act.key === "Edit" ? (
            <LuPen size={22} />
          ) : act.key === "canvas-zoom-in" ? (
            <LuZoomIn size={22} />
          ) : act.key === "canvas-zoom-out" ? (
            <LuZoomOut size={22} />
          ) : (
            <i className="material-icons" style={{ fontSize: "22px" }}>
              {act.icon}
            </i>
          )}
        </button>
      </OverlayTrigger>
    );
  };

  const renderLayerIcon = (key) => {
    switch (key) {
      case "BringtoFront":
        return (
          <svg
            width="25"
            height="20"
            viewBox="0 0 25 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M24.4718 11.5921L21.6976 9.99886L24.2069 8.55661C24.5866 8.33919 24.8211 7.93424 24.8211 7.50272C24.8211 7.07121 24.5849 6.66625 24.2069 6.44884L13.3969 0.23837C12.8434 -0.0786262 12.1566 -0.0802859 11.6031 0.23837L0.791428 6.4505C0.411745 6.66791 0.177185 7.07287 0.177185 7.50438C0.177185 7.9359 0.413432 8.34085 0.791428 8.55827L3.30071 10.0005L0.528181 11.5938C0.202498 11.7797 0 12.1249 0 12.4967C0 12.8684 0.202497 13.212 0.526493 13.3979L11.5964 19.7593C11.8697 19.917 12.1819 20 12.4992 20C12.8164 20 13.1286 19.917 13.402 19.7593L24.4718 13.3995C24.7975 13.2136 25 12.8684 25 12.4983C25 12.1282 24.7975 11.783 24.4735 11.5971L24.4718 11.5921ZM2.96659 7.50272L12.4992 2.02583L22.0317 7.50272L12.4992 12.9796L2.96659 7.50272Z"
              fill="#475569"
            />
          </svg>
        );
      case "Container":
        return (
          <svg
            width="21"
            height="20"
            viewBox="0 0 21 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect x="2" y="2" width="6" height="6" rx="1" stroke="#475569" strokeWidth="1.4" />
            <rect x="13" y="2" width="6" height="6" rx="1" stroke="#475569" strokeWidth="1.4" />
            <rect x="2" y="12" width="6" height="6" rx="1" stroke="#475569" strokeWidth="1.4" />
            <rect x="13" y="12" width="6" height="6" rx="1" stroke="#475569" strokeWidth="1.4" />
          </svg>
        );
      case "Ahead":
        return (
          <svg
            width="23"
            height="20"
            viewBox="0 0 23 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M22.4489 11.7714L19.4527 10.0008L22.4489 8.23024C22.7937 8.02643 23 7.6634 23 7.26057C23 6.85773 22.7937 6.49471 22.4489 6.2909L12.0605 0.152854C11.8905 0.0525436 11.6968 0 11.5 0C11.4465 0 11.3898 0.00477669 11.3268 0.0143301C11.267 0.0238834 11.2056 0.0398057 11.1442 0.0605047C11.0686 0.0859804 11.0009 0.116233 10.9395 0.152854L0.551068 6.28931C0.355832 6.40395 0.203109 6.57432 0.107066 6.77972C-0.0346373 7.08383 -0.0346373 7.43412 0.107066 7.73824C0.203109 7.94363 0.355832 8.114 0.551068 8.22864L3.5473 9.9992L0.551068 11.7698C0.206257 11.9736 0 12.3366 0 12.7394C0 13.1423 0.206257 13.5053 0.551068 13.7091L10.9395 19.8471C11.1095 19.9475 11.3032 20 11.5 20C11.6968 20 11.8905 19.9475 12.0605 19.8471L22.4489 13.7107C22.7937 13.5069 23 13.1439 23 12.741C23 12.3382 22.7937 11.9752 22.4489 11.7714ZM20.2714 12.741L11.5 17.9221L2.72857 12.741L5.44927 11.1345L10.2814 14.0037C10.3695 14.0562 10.4624 14.1024 10.5585 14.1438L10.8403 14.311C10.9505 14.3762 11.0717 14.424 11.2024 14.4559C11.4732 14.5195 11.7488 14.4973 12.0023 14.3906C12.0558 14.3683 12.1077 14.3412 12.1597 14.311L12.4384 14.1454C12.536 14.104 12.6289 14.0562 12.7171 14.0053L17.5586 11.1392L20.2714 12.741Z"
              fill="#475569"
            />
          </svg>
        );
      case "Behind":
        return (
          <svg
            width="21"
            height="20"
            viewBox="0 0 21 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M21 10.3531C21 9.97137 20.7987 9.6131 20.4766 9.42075L18.9252 8.4957L20.4766 7.57066C20.8001 7.37831 21 7.02004 21 6.63828C21 6.25652 20.7987 5.89825 20.4766 5.7059L11.2642 0.211438C10.7926 -0.0704794 10.2074 -0.0704794 9.73577 0.211438L0.523382 5.7059C0.199863 5.89825 0 6.25652 0 6.63828C0 7.02004 0.201301 7.37831 0.523382 7.57066L2.07484 8.4957L0.523382 9.42075C0.199863 9.6131 0 9.97137 0 10.3531C0 10.7349 0.201301 11.0932 0.523382 11.2855L1.55864 11.9037L0.543512 12.5336C0.232934 12.7186 0.0402602 13.0622 0.0402602 13.4293C0.0402602 13.7963 0.232934 14.1399 0.543512 14.3249L9.67395 19.7724C9.92414 19.9207 10.2088 20 10.4993 20C10.7897 20 11.0744 19.9207 11.3246 19.7724L20.455 14.3249C20.7656 14.1399 20.9583 13.7963 20.9583 13.4293C20.9583 13.0622 20.7656 12.7186 20.455 12.5336L19.4198 11.9154L20.4752 11.2855C20.7987 11.0932 20.9986 10.7349 20.9986 10.3531H21ZM10.4993 11.4837L2.37679 6.63828L10.4993 1.79282L18.6218 6.63828L10.4993 11.4837ZM3.78302 9.51472L9.65382 13.0167C10.1657 13.3221 10.8329 13.3221 11.3462 13.0167L17.217 9.51472L18.6232 10.3531L10.5007 15.1986L2.37823 10.3531L3.78446 9.51472H3.78302Z"
              fill="#475569"
            />
          </svg>
        );
      case "Theme":
        return (
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#475569"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="13.5" cy="6.5" r=".5" fill="#475569" stroke="none" />
            <circle cx="17.5" cy="10.5" r=".5" fill="#475569" stroke="none" />
            <circle cx="8.5" cy="7.5" r=".5" fill="#475569" stroke="none" />
            <circle cx="6.5" cy="12.5" r=".5" fill="#475569" stroke="none" />
            <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
          </svg>
        );
      case "SendtoBack":
      default:
        return (
          <svg
            width="21"
            height="20"
            viewBox="0 0 21 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20.9986 9.96749C20.9986 9.6 20.7972 9.25513 20.4751 9.06997L18.9236 8.17951L20.4751 7.28905C20.7987 7.10389 20.9986 6.75901 20.9986 6.39152C20.9986 6.02403 20.7972 5.67915 20.4751 5.49399L11.2636 0.203534C10.7919 -0.0678445 10.2067 -0.0678445 9.735 0.203534L0.52342 5.49399C0.199879 5.67915 0 6.02403 0 6.39152C0 6.75901 0.201317 7.10389 0.52342 7.28905L2.07498 8.17951L0.52342 9.06997C0.199879 9.25513 0 9.6 0 9.96749C0 10.335 0.201317 10.6799 0.52342 10.865L2.15263 11.8007L0.52342 12.7364C0.199879 12.9216 0 13.2664 0 13.6339C0 14.0014 0.201317 14.3463 0.52342 14.5315L9.65448 19.7753C9.91044 19.9223 10.2023 20 10.5 20C10.7977 20 11.0896 19.9223 11.3455 19.7753L20.4766 14.5315C20.8001 14.3463 21 14.0014 21 13.6339C21 13.2664 20.7987 12.9216 20.4766 12.7364L18.8474 11.8007L20.4766 10.865C20.8001 10.6799 21 10.335 21 9.96749H20.9986ZM3.85805 12.7816L9.6516 16.1088C10.1635 16.4028 10.8307 16.4028 11.3426 16.1088L17.1362 12.7816L18.6202 13.6339L10.4971 18.2982L2.37408 13.6339L3.85805 12.7816ZM10.4971 14.6318L2.37408 9.96749L3.77897 9.16043L9.65016 12.5314C9.90612 12.6784 10.198 12.7562 10.4957 12.7562C10.7933 12.7562 11.0852 12.6784 11.3412 12.5314L17.2124 9.16043L18.6187 9.96749L10.4957 14.6318H10.4971Z"
              fill="#475569"
            />
          </svg>
        );
    }
  };

  const createAlert = (state, callback) => {
    const actions = ["copy", "paste"];
    const shortCuts = ["C", "V"];
    return (
      <Alert show={state} onClose={callback} className="pageModal" key="alert">
        <p>{i18n.t("clipboard.msg")}</p>
        {actions.map((act, ind) => {
          return (
            <Col xs={4} key={ind}>
              <h2>Ctrl+{shortCuts[ind]}</h2>
              <div>
                {i18n.t("clipboard.to")} {i18n.t("clipboard." + actions[ind]).toLowerCase()}
              </div>
            </Col>
          );
        })}
        <br />
      </Alert>
    );
  };

  return (
    <Col
      id="ActionRibbon"
      md="auto"
      xs="auto"
      style={{
        height: props.ribbonHeight,
        overflowY: "hidden",
        background: "white",
        borderRadius: 14,
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        padding: "0 8px",
        pointerEvents: "auto",
      }}
      ref={holderRef}
    >
      <div className="actions">
        {slide && (
          <div className="actions-layers">
            {layerActions
              .filter((act) => act !== "separator")
              .map((act) => (
                <OverlayTrigger
                  key={act.key}
                  placement="bottom"
                  overlay={
                    <Tooltip id={`layer-tooltip-${act.key}`}>
                      {i18n.t(act.i18nkey) || act.key}
                    </Tooltip>
                  }
                >
                  <button
                    type="button"
                    className="layerIconBtn"
                    disabled={act.disabled}
                    name={act.key}
                    onClick={() => {
                      act.onClick();
                      document.activeElement.blur();
                    }}
                  >
                    {renderLayerIcon(act.key)}
                  </button>
                </OverlayTrigger>
              ))}
          </div>
        )}
        <div>
          <Clipboard
            ref={props.clipboardRef}
            boxes={props.boxes}
            key="clipboard"
            boxSelected={props.boxSelected}
            openTextEditor={props.openTextEditor}
            boxSelectedAll={props.boxSelectedAll}
            navItemSelected={props.navItemSelected}
            containedViewSelected={props.containedViewSelected}
            navItems={props.navItems}
            containedViews={props.containedViews}
            toolbars={props.pluginToolbars}
            marks={props.marks}
            exercises={props.exercises}
            onBoxPasted={props.onBoxPasted}
            onBoxAdded={props.onBoxAdded}
            uploadFunction={props.uploadFunction}
            onBoxDeleted={props.onBoxDeleted}
            viewToolbars={props.viewToolbars}
            animations={props.animations}
            soundList={props.soundList}
            onScenePasted={props.onScenePasted}
            onNavItemSelected={props.onNavItemSelected}
            onSave={props.onSave}
          >
            {clipboardActions.map((act) => {
              return button(act);
            })}
          </Clipboard>

          {createAlert(clipboardAlert, onClick)}
          <ThemePaletteModal
            show={showThemeModal}
            onClose={() => setShowThemeModal(false)}
            onAddImageModalToggle={props.onAddImageModalToggle}
          />
          <HtmlEditModal
            show={showHtmlEditModal}
            onClose={() => setShowHtmlEditModal(false)}
            initialHtml={
              selectedPluginToolbar && selectedPluginToolbar.state
                ? selectedPluginToolbar.state._html || ""
                : ""
            }
            onSave={(sanitizedHtml) => {
              if (!selectedBoxObj || !selectedPluginToolbar) return;
              const newState = { ...selectedPluginToolbar.state, _html: sanitizedHtml };
              const apiPlugin = IdealStudio.Plugins.get(selectedPluginToolbar.pluginId);
              const newToolbar = apiPlugin ? apiPlugin.getToolbar(newState) : selectedPluginToolbar;
              dispatch(updateBox(selectedBoxObj.id, selectedBoxObj.content, newToolbar, newState));
            }}
          />
        </div>
      </div>
    </Col>
  );
};

export default ActionsRibbon;

ActionsRibbon.propTypes = {
  /**
   * Current selected view (by ID)
   */
  navItemSelected: PropTypes.any,
  /**
   * Selected contained view (by ID)
   */
  containedViewSelected: PropTypes.any,
  /**
   * Object containing all views (by id)
   */
  navItems: PropTypes.object,
  /**
   * Object containing all contained views (identified by its ID)
   */
  containedViews: PropTypes.object,
  /**
   * Height of the ribbon in px (contains the string px)
   */
  ribbonHeight: PropTypes.string,
  /**
   * Toggle grid on slides function
   */
  onContainerToggle: PropTypes.func,
  /**
   * Whether grid is disabled or not
   */
  grid: PropTypes.bool,
  /**
   * Selected box
   */
  boxSelected: PropTypes.any,
  /**
   * Object containing all created boxes (by id)
   */
  boxes: PropTypes.any,
  /**
   * Callback for changing box layers
   */
  onBoxLayerChanged: PropTypes.any,
  /**
   * Callback for pasting a box
   */
  onBoxPasted: PropTypes.any,
  /**
   * Callback for deleting a box
   */
  onBoxDeleted: PropTypes.any,
  /**
   * Object containing all plugins' toolbars
   */
  pluginToolbars: PropTypes.object,
  /**
   * Object containing all marks
   */
  marks: PropTypes.object,
  /**
   * Callback for adding a box
   */
  onBoxAdded: PropTypes.func.isRequired,
  /**
   * Object containing all the exercises
   */
  exercises: PropTypes.object.isRequired,
  /**
   *  Function for uploading a file to the server
   */
  uploadFunction: PropTypes.func.isRequired,
  /**
   * Forwarded to the Theme & Palette modal so the Custom/Image sub-mode can
   * launch the image picker (AddImageModal) for a background image.
   */
  onAddImageModalToggle: PropTypes.func,
  /**
   * Triggered when the Edit button targets a needsTextEdition plugin.
   */
  onTextEditorToggled: PropTypes.func,
  /**
   * Triggered when the Edit button targets a needsConfigModal plugin.
   */
  openConfigModal: PropTypes.func,
  /**
   * Multi-select state from Redux; the Edit button stays disabled when more
   * than one box is selected.
   */
  boxSelectedAll: PropTypes.array,
  /**
   * Called with "in" or "out" when the canvas zoom buttons are clicked.
   */
  onCanvasZoom: PropTypes.func,
  /**
   * Current canvas zoom level — used to disable the zoom buttons at limits.
   */
  canvasZoom: PropTypes.number,
  canvasZoomMin: PropTypes.number,
  canvasZoomMax: PropTypes.number,
  /** viewToolbarsById — forwarded to Clipboard for scene copy */
  viewToolbars: PropTypes.object,
  /** animationsById — forwarded to Clipboard for scene copy */
  animations: PropTypes.object,
  /** soundList keyed by sceneId — forwarded to Clipboard for scene copy */
  soundList: PropTypes.object,
  /** Called with rekeyed scene after paste confirmed */
  onScenePasted: PropTypes.func,
  /** Selects a nav item by id after scene inserted */
  onNavItemSelected: PropTypes.func,
  /** Triggers editor save after scene paste */
  onSave: PropTypes.func,
};
